import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Zap, Shield, TrendingUp, Users, Heart, Sparkles, ChevronDown, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SAAS_WHATSAPP = '5565984652720'; // DDI 55 + DDD 65 + número
const WHATSAPP_MSG = encodeURIComponent('Olá! Vi a plataforma Giraia Bike Shop e quero saber mais sobre ativar para o meu MC ou Moto Grupo. 🏍️');

const SaaSLandingPage = () => {
  const [planos, setPlanos] = useState([]);
  const [descontoAnualPct, setDescontoAnualPct] = useState(15);
  const [periodo, setPeriodo] = useState('mensal'); // mensal | anual
  const [form, setForm] = useState({
    nome_mc: '', nome_responsavel: '', cidade: '', estado: '',
    whatsapp: '', email: '', total_integrantes: '', plano_interesse: '', mensagem: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/saas/planos`)
      .then(r => {
        setPlanos(r.data.planos);
        setDescontoAnualPct(r.data.desconto_anual_pct || 15);
      })
      .catch(() => {});
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const submit = async () => {
    if (!form.nome_mc || !form.nome_responsavel || !form.cidade || !form.estado || !form.whatsapp) {
      toast.error('Preencha os campos obrigatórios.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/public/saas/leads`, {
        ...form,
        total_integrantes: form.total_integrantes ? parseInt(form.total_integrantes) : null,
      });
      setSent(true);
      toast.success('Recebemos seu contato! 💜');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-lg border-b border-[#2D243F]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="saas-nav-home" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E2B84A] to-[#FFD700] flex items-center justify-center font-black text-black">M</div>
            <span className="font-black text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>MANAS <span className="text-[#FFD700]">MC</span></span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <button onClick={() => scrollTo('planos')} data-testid="saas-nav-planos" className="text-[#A19BAA] hover:text-white hidden md:inline">Planos</button>
            <button onClick={() => scrollTo('recursos')} data-testid="saas-nav-recursos" className="text-[#A19BAA] hover:text-white hidden md:inline">Recursos</button>
            <a href={`https://wa.me/${SAAS_WHATSAPP}?text=${WHATSAPP_MSG}`} target="_blank" rel="noreferrer"
              data-testid="saas-nav-wpp-mobile"
              className="md:hidden bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full px-3 py-2 font-bold text-xs flex items-center gap-1">
              <MessageCircle size={14} /> WhatsApp
            </a>
            <button onClick={() => scrollTo('contato')} data-testid="saas-nav-contato" className="bg-[#E2B84A] hover:bg-[#F0D98C] rounded-full px-4 py-2 font-bold hidden md:inline">Quero Conhecer</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E2B84A]/20 via-transparent to-[#FFD700]/10 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 relative">
          <div className="inline-flex items-center gap-2 bg-[#120E1A] border border-[#E2B84A]/40 rounded-full px-4 py-1.5 text-xs mb-6">
            <Sparkles size={14} className="text-[#FFD700]" />
            <span className="text-[#A19BAA]">A plataforma usada pela <strong className="text-white">Giraia Bike Shop</strong> agora disponível para o seu MC ou Moto Grupo</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="saas-hero-title">
            O <span className="text-[#E2B84A]">sistema completo</span> para<br/>
            profissionalizar seu <span className="text-[#FFD700]">Motoclube ou Moto Grupo</span>.
          </h1>
          <p className="text-base md:text-lg text-[#A19BAA] max-w-2xl mb-8">
            Carteirinha digital, mensalidades automatizadas, eventos com check-in GPS, loja interna, gamificação, flyers com IA e muito mais — tudo no mesmo app, com a sua identidade visual. Vale para MCs oficiais e moto grupos informais.
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => scrollTo('contato')} data-testid="saas-hero-cta" className="bg-[#FFD700] hover:bg-[#F4C430] text-black font-black rounded-full px-6 py-3 flex items-center gap-2">
              Solicitar Demonstração <ArrowRight size={18} />
            </button>
            <button onClick={() => scrollTo('planos')} data-testid="saas-hero-planos" className="border border-[#2D243F] hover:border-[#E2B84A] text-white font-bold rounded-full px-6 py-3">
              Ver Planos
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            <Stat n="14 dias" label="Trial grátis" />
            <Stat n="100%" label="Em Português" />
            <Stat n="PT-BR" label="Pagamento PIX" />
            <Stat n="LGPD" label="Dados seguros" />
          </div>
        </div>
      </section>

      {/* RECURSOS */}
      <section id="recursos" className="py-20 bg-[#0a0614]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Tudo que seu MC ou Moto Grupo precisa</h2>
          <p className="text-[#A19BAA] text-center mb-12 max-w-2xl mx-auto">Desenhado por motociclistas, para motociclistas. Chega de planilhas, grupos de WhatsApp bagunçados e controle feito na mão.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Feature icon={<Users />} title="Gestão de Integrantes" desc="Cadastro completo com CNH, carteirinha digital e matrícula automática. Controle de ativas, pendentes e inadimplentes." />
            <Feature icon={<TrendingUp />} title="Livro Caixa Digital" desc="Entradas, saídas, comprovantes obrigatórios e relatórios em PDF mensais e anuais." />
            <Feature icon={<Zap />} title="Flyers com IA" desc="7 templates prontos gerados por IA (Nano Banana). Só preenche a data e o endereço — flyer pronto para o Instagram." />
            <Feature icon={<Shield />} title="Botão SOS" desc="Emergência com geolocalização enviada para a diretoria. Segurança das integrantes em primeiro lugar." />
            <Feature icon={<Heart />} title="Gamificação" desc="Ranking por pontos, conquistas e madrinha premiada. Engajamento real nos eventos." />
            <Feature icon={<Sparkles />} title="Check-in GPS em Eventos" desc="Lista de presença automática por proximidade + assinatura digital em Ata PDF." />
          </div>
        </div>
      </section>

      {/* PROVA VISUAL — Screenshots reais */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#FFD700]/20 text-[#FFD700] text-xs font-black px-3 py-1 rounded-full mb-3">100% REAL · SEM MOCKUP</span>
            <h2 className="text-3xl md:text-4xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Veja a plataforma funcionando</h2>
            <p className="text-[#A19BAA] max-w-2xl mx-auto">Todas as telas abaixo são do sistema em produção, usado hoje pela Giraia Bike Shop Sorriso-MT.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <ScreenshotCard src={`${API_URL}/api/public/saas/shot/carteirinha`} title="Carteirinha Digital" desc="ID oficial com QR Code para lojas parceiras" />
            <ScreenshotCard src={`${API_URL}/api/public/saas/shot/dashboard`} title="Dashboard" desc="Acesso rápido a tudo que a integrante precisa" />
            <ScreenshotCard src={`${API_URL}/api/public/saas/shot/flyers`} title="Flyers com IA" desc="7 templates prontos pra postar no Insta" />
            <ScreenshotCard src={`${API_URL}/api/public/saas/shot/admin`} title="Painel Admin" desc="Aprovações, mensalidades, livro caixa, produtos" />
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section id="planos" className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Escolha o plano do seu MC ou Moto Grupo</h2>
          <p className="text-[#A19BAA] text-center mb-8">Sem taxa de setup. Cancele quando quiser. 14 dias grátis em qualquer plano.</p>

          {/* Toggle mensal/anual */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-[#120E1A] border border-[#2D243F] rounded-full p-1" data-testid="plano-periodo-toggle">
              <button
                onClick={() => setPeriodo('mensal')}
                data-testid="periodo-mensal"
                className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${periodo === 'mensal' ? 'bg-[#E2B84A] text-white' : 'text-[#A19BAA]'}`}
              >
                Mensal
              </button>
              <button
                onClick={() => setPeriodo('anual')}
                data-testid="periodo-anual"
                className={`px-5 py-2 rounded-full text-sm font-bold transition-colors flex items-center gap-2 ${periodo === 'anual' ? 'bg-[#FFD700] text-black' : 'text-[#A19BAA]'}`}
              >
                Anual
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">-{descontoAnualPct}%</span>
              </button>
            </div>
          </div>

          {planos.length === 0 ? (
            <p className="text-center text-[#716A80]">Carregando planos…</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-5">
              {planos.map((p) => {
                const destaque = p.id === 'pro';
                const mostraAnual = periodo === 'anual';
                const valor = mostraAnual ? p.preco_anual_equivalente_mensal : p.preco_mensal;
                return (
                  <div key={p.id} data-testid={`plano-${p.id}`}
                    className={`relative bg-[#120E1A] border rounded-2xl p-6 ${destaque ? 'border-[#FFD700] shadow-[0_0_40px_rgba(255,215,0,0.15)]' : 'border-[#2D243F]'}`}>
                    {destaque && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFD700] text-black text-xs font-black px-3 py-1 rounded-full">MAIS ESCOLHIDO</span>
                    )}
                    <h3 className="text-2xl font-black mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.nome}</h3>
                    <p className="text-[#A19BAA] text-sm mb-4">{p.descricao}</p>
                    <div className="mb-1">
                      <span className="text-4xl font-black text-white">R$ {(valor || 0).toFixed(2).replace('.', ',')}</span>
                      <span className="text-[#A19BAA] text-sm">/mês</span>
                    </div>
                    {mostraAnual && (
                      <p className="text-xs text-green-400 mb-4" data-testid={`plano-${p.id}-anual-info`}>
                        Pagamento anual: R$ {(p.preco_anual || 0).toFixed(2).replace('.', ',')} · economia de R$ {(p.economia_anual || 0).toFixed(2).replace('.', ',')}
                      </p>
                    )}
                    {!mostraAnual && <p className="text-xs text-[#716A80] mb-4">ou R$ {(p.preco_anual || 0).toFixed(2).replace('.', ',')}/ano (-{descontoAnualPct}%)</p>}
                    <ul className="space-y-2 mb-6 text-sm">
                      {p.recursos.map((r, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-[#E7E3EC]">{r}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => { setForm(f => ({ ...f, plano_interesse: p.id })); scrollTo('contato'); }}
                      data-testid={`plano-cta-${p.id}`}
                      className={`w-full font-black rounded-lg py-3 transition-colors ${
                        destaque ? 'bg-[#FFD700] hover:bg-[#F4C430] text-black' : 'bg-[#E2B84A] hover:bg-[#F0D98C] text-white'
                      }`}>
                      Quero este plano
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CONTATO */}
      <section id="contato" className="py-20 bg-[#0a0614]">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Converse com a gente</h2>
          <p className="text-[#A19BAA] text-center mb-10">Preencha o formulário e nossa equipe entra em contato em até 24h pelo WhatsApp.</p>

          {sent ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-10 text-center" data-testid="saas-lead-success">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-2xl font-black mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Recebemos seu contato!</h3>
              <p className="text-green-300">Em até 24h uma de nossas consultoras vai te chamar no WhatsApp. 💜</p>
            </div>
          ) : (
            <div className="bg-[#120E1A] border border-[#2D243F] rounded-2xl p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nome do MC ou Moto Grupo *" value={form.nome_mc} onChange={v => setForm({ ...form, nome_mc: v })} testid="lead-nome-mc" />
                <Field label="Seu nome *" value={form.nome_responsavel} onChange={v => setForm({ ...form, nome_responsavel: v })} testid="lead-nome-resp" />
              </div>
              <div className="grid md:grid-cols-[1fr_100px] gap-4">
                <Field label="Cidade *" value={form.cidade} onChange={v => setForm({ ...form, cidade: v })} testid="lead-cidade" />
                <Field label="UF *" value={form.estado} onChange={v => setForm({ ...form, estado: v.toUpperCase().slice(0, 2) })} testid="lead-estado" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="WhatsApp *" value={form.whatsapp} onChange={v => setForm({ ...form, whatsapp: v })} testid="lead-wpp" />
                <Field label="E-mail (opcional)" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="lead-email" type="email" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nº de integrantes" value={form.total_integrantes} onChange={v => setForm({ ...form, total_integrantes: v.replace(/\D/g, '') })} testid="lead-total" type="number" />
                <div>
                  <label className="block text-[#E7E3EC] text-sm font-bold mb-1">Plano de interesse</label>
                  <select value={form.plano_interesse} onChange={e => setForm({ ...form, plano_interesse: e.target.value })}
                    data-testid="lead-plano" className="w-full bg-[#050505] border border-[#2D243F] rounded-lg px-4 py-2.5 text-white">
                    <option value="">Ainda não decidi</option>
                    {planos.map(p => <option key={p.id} value={p.id}>{p.nome} · R$ {p.preco_mensal.toFixed(2).replace('.', ',')}/mês</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[#E7E3EC] text-sm font-bold mb-1">Mensagem</label>
                <textarea value={form.mensagem} onChange={e => setForm({ ...form, mensagem: e.target.value })} rows={3}
                  data-testid="lead-mensagem"
                  placeholder="Conta pra gente: há quanto tempo o MC existe, o que vocês mais precisam resolver?"
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-lg px-4 py-2.5 text-white" />
              </div>
              <button onClick={submit} disabled={sending}
                data-testid="lead-submit"
                className="w-full bg-[#FFD700] hover:bg-[#F4C430] text-black font-black rounded-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
                {sending ? 'Enviando...' : <>Enviar <ArrowRight size={18} /></>}
              </button>
              <p className="text-xs text-[#716A80] text-center">Ao enviar, você concorda em ser contatado pela nossa equipe via WhatsApp.</p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ simples */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>Perguntas Frequentes</h2>
          <div className="space-y-3">
            <FAQ q="Posso customizar com o logo e cores do meu MC ou moto grupo?" a="Sim. No plano Elite a personalização visual é total (logo, cores, nome do app). Starter e Pro usam a marca do seu grupo mas com layout padrão." />
            <FAQ q="Como funciona o período de trial?" a="Ao assinar qualquer plano, você tem 14 dias grátis para testar tudo. Não cobramos nada nesse período." />
            <FAQ q="E se eu quiser cancelar?" a="Você cancela a qualquer momento pelo app. Sem multa, sem pergunta." />
            <FAQ q="Como é feito o pagamento?" a="Mensalmente via PIX ou cartão. Também aceitamos pagamento anual com 15% de desconto." />
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2D243F] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-3 items-center text-sm text-[#716A80]">
          <p>© 2026 Giraia Bike Shop · Plataforma SaaS para Motoclubes</p>
          <Link to="/" className="hover:text-white">Voltar ao app principal</Link>
        </div>
      </footer>

      {/* Botão WhatsApp Flutuante (sempre visível) */}
      <a
        href={`https://wa.me/${SAAS_WHATSAPP}?text=${WHATSAPP_MSG}`}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp"
        className="fixed bottom-5 right-5 z-50 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full shadow-2xl p-4 flex items-center gap-2 group transition-all hover:scale-105"
        aria-label="Fale no WhatsApp"
        style={{ boxShadow: '0 10px 30px rgba(37, 211, 102, 0.4)' }}
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden md:inline font-bold text-sm pr-1">Fale no WhatsApp</span>
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center animate-pulse">1</span>
      </a>
    </div>
  );
};

const ScreenshotCard = ({ src, title, desc }) => (
  <div className="group relative bg-[#120E1A] border border-[#2D243F] rounded-xl overflow-hidden hover:border-[#FFD700]/50 transition-all hover:-translate-y-1">
    <div className="relative bg-[#050505] aspect-[9/16] overflow-hidden">
      <img
        src={src}
        alt={title}
        loading="lazy"
        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    </div>
    <div className="p-4">
      <h3 className="font-black text-white text-sm mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
      <p className="text-[#A19BAA] text-xs">{desc}</p>
    </div>
  </div>
);

const Stat = ({ n, label }) => (
  <div>
    <p className="text-2xl md:text-3xl font-black text-[#FFD700]" style={{ fontFamily: 'Outfit, sans-serif' }}>{n}</p>
    <p className="text-xs text-[#A19BAA]">{label}</p>
  </div>
);

const Feature = ({ icon, title, desc }) => (
  <div className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-5 hover:border-[#E2B84A]/50 hover:-translate-y-1 transition-all">
    <div className="w-10 h-10 rounded-lg bg-[#E2B84A]/20 text-[#E2B84A] flex items-center justify-center mb-3">{icon}</div>
    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
    <p className="text-[#A19BAA] text-sm">{desc}</p>
  </div>
);

const Field = ({ label, value, onChange, testid, type = 'text' }) => (
  <div>
    <label className="block text-[#E7E3EC] text-sm font-bold mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-[#050505] border border-[#2D243F] rounded-lg px-4 py-2.5 text-white" />
  </div>
);

const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#120E1A] border border-[#2D243F] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center p-4 text-left">
        <span className="font-bold text-white">{q}</span>
        <ChevronDown size={18} className={`text-[#A19BAA] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-4 pb-4 text-[#A19BAA] text-sm">{a}</p>}
    </div>
  );
};

export default SaaSLandingPage;
